import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import api from '../../services/api';

const SocialShare = ({ product, className = '', autoOpen = false, onClose }) => {
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);

  // Auto-open modal when autoOpen prop is true
  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true);
    }
  }, [autoOpen]);

  if (!product) return null;

  // Format price with proper currency
  const formatPrice = (price, currency) => {
    if (!price) return '';

    // Currency symbol mapping
    const currencySymbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'CNY': '¥',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'PLN': 'zł',
      'CZK': 'Kč',
      'HUF': 'Ft',
      'RUB': '₽',
      'BRL': 'R$',
      'MXN': '$',
      'INR': '₹',
      'KRW': '₩',
      'SGD': 'S$',
      'HKD': 'HK$',
      'NZD': 'NZ$',
      'ZAR': 'R',
      'TRY': '₺'
    };

    const symbol = currencySymbols[currency] || currency || '€';

    // Format number with proper decimal places
    const formattedPrice = parseFloat(price).toFixed(2);

    // For some currencies, put symbol after the number
    const symbolAfter = ['SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'TRY'];

    if (symbolAfter.includes(currency)) {
      return `${formattedPrice} ${symbol}`;
    }

    return `${symbol}${formattedPrice}`;
  };

  // Create or get shareable URL from backend
  useEffect(() => {
    const createShareUrl = async () => {
      try {
        setLoading(true);
        const response = await api.post('/shares/', {
          content_type: 'product',
          object_id: product.id,
          privacy: 'PUBLIC'
        });

        if (response.data && response.data.preview && response.data.preview.share_url) {
          // Use the universal share URL from the backend response
          setShareUrl(response.data.preview.share_url);
        } else if (response.data && response.data.preview) {
          // API returned preview but no share_url - this shouldn't happen with universal links
          console.warn('API returned preview without share_url:', response.data.preview);
          setShareUrl(`${window.location.origin}/products/${product.slug}`);
        } else {
          // API didn't return expected structure - this shouldn't happen
          console.error('Unexpected API response structure:', response.data);
          setShareUrl(`${window.location.origin}/products/${product.slug}`);
        }
      } catch (error) {
        console.error('Failed to create share URL:', error);
        // Fallback to direct product URL for public content
        setShareUrl(`${window.location.origin}/products/${product.slug}`);
      } finally {
        setLoading(false);
      }
    };

    if (product.id && product.slug) {
      createShareUrl();
    }
  }, [product.id, product.slug]);

  const currentUrl = shareUrl || window.location.href;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleNativeShare = async () => {
    // Temporarily disable native share to show custom modal
    // Change this back to `if (navigator.share)` for production
    if (false && navigator.share) {
      try {
        await navigator.share({
          title: `${product.name} - Perfect Gift | Giftsy`,
          text: product.description || `Check out ${product.name} on Giftsy!`,
          url: currentUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      setIsOpen(!isOpen);
    }
  };

  // Generate rich sharing content
  const generateRichShareContent = () => {
    const productTitle = product.name;
    const productDescription = product.description || '';
    const price = product.current_price ? formatPrice(product.current_price, product.price_currency) : '';
    const successRate = product.success_rate ? `${product.success_rate}% gift success rate` : '';
    const brand = product.brand ? `by ${product.brand}` : '';

    // Create optimized text for WhatsApp (short to avoid "Read more")
    const shortText = [
      productTitle,
      brand,
      price,
      successRate ? `${successRate} - Perfect Gift!` : 'Perfect Gift!'
    ].filter(Boolean).join('\n');

    // Create rich text for other platforms
    const richText = [
      productTitle,
      brand,
      productDescription.substring(0, 100) + (productDescription.length > 100 ? '...' : ''),
      '',  // Empty line for spacing
      price,
      successRate,
      'Perfect Gift | Giftsy'
    ].filter(item => item !== undefined && item !== null && item !== '').join('\n');

    return {
      title: `${productTitle} - Perfect Gift | Giftsy`,
      description: productDescription,
      richText: richText,
      shortText: shortText,
      shortTitle: productTitle,
      price: price
    };
  };

  // Generate sharing URLs dynamically with rich content
  const generateSharingUrls = (url, content) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(content.title);
    const encodedDescription = encodeURIComponent(content.description);
    const encodedRichText = encodeURIComponent(content.richText);
    const encodedShortText = encodeURIComponent(content.shortText);
    const encodedImage = encodeURIComponent(product.images?.[0]?.image || '');

    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedDescription}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}&hashtags=gifts,giftsy`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedDescription}&media=${encodedImage}`,
      whatsapp: `https://wa.me/?text=${encodedShortText}%0A%0A${encodedUrl}`,
      email: `mailto:?subject=${encodedTitle}&body=${content.richText}%0A%0A${encodedUrl}`,
      reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
      vk: `https://vk.com/share.php?url=${encodedUrl}&title=${encodedTitle}&description=${encodedDescription}`,
      ok: `https://connect.ok.ru/offer?url=${encodedUrl}&title=${encodedTitle}`,
      blogger: `https://www.blogger.com/blog-this.g?u=${encodedUrl}&n=${encodedTitle}&t=${encodedDescription}`,
      tumblr: `https://www.tumblr.com/share/link?url=${encodedUrl}&name=${encodedTitle}&description=${encodedDescription}`,
      kakaotalk: `kakaolink://send?text=${encodedShortText}%0A%0A${encodedUrl}`
    };
  };

  const sharingUrls = generateSharingUrls(
    currentUrl,
    generateRichShareContent()
  );

  // YouTube-style social platforms with more options
  const socialPlatforms = [
    {
      name: 'Share',
      icon: 'Share2',
      action: 'native',
      color: 'bg-gray-600 hover:bg-gray-700'
    },
    {
      name: 'Reddit',
      icon: 'MessageSquare',
      url: sharingUrls.reddit,
      color: 'bg-orange-600 hover:bg-orange-700'
    },
    {
      name: 'WhatsApp',
      icon: 'MessageCircle',
      url: sharingUrls.whatsapp,
      color: 'bg-green-600 hover:bg-green-700'
    },
    {
      name: 'VK',
      icon: 'Users',
      url: sharingUrls.vk,
      color: 'bg-blue-800 hover:bg-blue-900'
    },
    {
      name: 'OK',
      icon: 'Circle',
      url: sharingUrls.ok,
      color: 'bg-orange-500 hover:bg-orange-600'
    },
    {
      name: 'Pinterest',
      icon: 'Image',
      url: sharingUrls.pinterest,
      color: 'bg-red-600 hover:bg-red-700'
    },
    {
      name: 'Facebook',
      icon: 'Facebook',
      url: sharingUrls.facebook,
      color: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      name: 'X',
      icon: 'Twitter',
      url: sharingUrls.twitter,
      color: 'bg-black hover:bg-gray-800'
    },
    {
      name: 'Blogger',
      icon: 'Edit3',
      url: sharingUrls.blogger,
      color: 'bg-orange-400 hover:bg-orange-500'
    },
    {
      name: 'Tumblr',
      icon: 'FileText',
      url: sharingUrls.tumblr,
      color: 'bg-indigo-600 hover:bg-indigo-700'
    },
    {
      name: 'Email',
      icon: 'Mail',
      url: sharingUrls.email,
      color: 'bg-gray-600 hover:bg-gray-700'
    },
    {
      name: 'KakaoTalk',
      icon: 'MessageCircle',
      url: sharingUrls.kakaotalk,
      color: 'bg-yellow-500 hover:bg-yellow-600'
    }
  ];

  const handlePlatformClick = (platform) => {
    if (platform.action === 'native') {
      handleNativeShare();
    } else if (platform.url) {
      window.open(platform.url, '_blank', 'noopener,noreferrer');
    }
    setIsOpen(false);
    if (onClose) onClose();
  };

  return (
    <>
      {/* Only show trigger button when NOT auto-opening */}
      {!autoOpen && (
        <div className={`relative ${className}`}>
          <Button
            variant="secondary"
            size="sm"
            iconName="Share2"
            onClick={handleNativeShare}
            className="flex items-center space-x-2"
            disabled={loading}
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
            ) : (
              <span>Share</span>
            )}
          </Button>
        </div>
      )}

      {/* YouTube-style Modal Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setIsOpen(false); if (onClose) onClose(); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">Share</h2>
              <button
                onClick={() => { setIsOpen(false); if (onClose) onClose(); }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Icon name="X" size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Share URL Section */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3">
                <input
                  type="text"
                  value={currentUrl}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-gray-700 outline-none"
                />
                <Button
                  variant={copied ? "success" : "primary"}
                  size="sm"
                  onClick={handleCopyLink}
                  className="px-4 py-2"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>

            {/* Social Platforms Grid */}
            <div className="p-6">
              <div className="grid grid-cols-4 gap-4">
                {socialPlatforms.map((platform) => (
                  <button
                    key={platform.name}
                    onClick={() => handlePlatformClick(platform)}
                    className="flex flex-col items-center space-y-2 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                  >
                    <div className={`w-12 h-12 ${platform.color} rounded-full flex items-center justify-center group-hover:scale-105 transition-transform`}>
                      <Icon name={platform.icon} size={20} className="text-white" />
                    </div>
                    <span className="text-xs text-gray-600 text-center">{platform.name}</span>
                  </button>
                ))}
              </div>

              {/* Copy Link Option */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center justify-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center">
                    <Icon name="Link" size={20} className="text-white" />
                  </div>
                  <span className="text-sm text-gray-700">Copy link</span>
                  {copied && (
                    <Icon name="Check" size={16} className="text-green-600" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SocialShare;